import { useState, useCallback, useRef, useEffect } from 'react';
import { Platform, Alert } from 'react-native';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';

// Conditionally import Voice only on native platforms
let Voice: any = null;
if (Platform.OS !== 'web') {
  try {
    Voice = require('@react-native-voice/voice').default;
  } catch (error) {
    console.warn('Voice module not available:', error);
  }
}

export interface UseVoiceTypingOptions {
  /**
   * Callback when text is updated via voice input
   * @param text - The updated text
   */
  onTextUpdate?: (text: string) => void;
  
  /**
   * Callback to get current text from input field
   * @returns Current text in the input field
   */
  getCurrentText?: () => string;
  
  /**
   * Callback to set text in input field (for web platform)
   * @param text - Text to set
   */
  setTextInInput?: (text: string) => void;
  
  /**
   * Whether input is currently disabled (e.g., awaiting response)
   * When true, voice recording will be stopped and prevented from starting
   */
  inputDisabled?: boolean;
  
  /**
   * Callback when user manually edits the input (should stop voice recording)
   * @param text - The new text after manual edit
   */
  onManualEdit?: (text: string) => void;
}

export interface UseVoiceTypingReturn {
  /**
   * Whether voice recording is currently active
   */
  isRecording: boolean;
  
  /**
   * Text from voice input (for syncing with input field)
   */
  voiceInputText: string;
  
  /**
   * Start voice typing/recording
   */
  startVoiceTyping: () => Promise<void>;
  
  /**
   * Stop voice typing/recording
   */
  stopVoiceTyping: () => Promise<void>;
  
  /**
   * Toggle voice typing on/off
   */
  handleVoiceButtonPress: () => Promise<void>;
  
  /**
   * Clear voice input text (call this when message is sent)
   * Also stops recording if active and ignores any pending voice results
   */
  clearVoiceInput: () => Promise<void>;
  
  /**
   * Handle manual text edits (call this from messageSetter to stop voice when user types)
   * @param text - The new text after manual edit
   */
  handleManualEdit: (text: string) => Promise<void>;
}

/**
 * Custom hook for voice typing functionality
 * Supports both web (Web Speech API) and native (React Native Voice) platforms
 * 
 * @param options - Configuration options for voice typing
 * @returns Voice typing state and control functions
 */
export const useVoiceTyping = (options: UseVoiceTypingOptions = {}): UseVoiceTypingReturn => {
  const { onTextUpdate, getCurrentText, setTextInInput, inputDisabled = false, onManualEdit } = options;
  
  const [isRecording, setIsRecording] = useState(false);
  const [voiceInputText, setVoiceInputText] = useState('');
  const inputToolbarTextRef = useRef<string>('');
  const recognitionRef = useRef<any>(null);
  const lastProcessedTextRef = useRef<string>(''); // Track last processed transcription to avoid duplicates
  const ignoreVoiceResultsRef = useRef<boolean>(false); // Flag to ignore voice results after clearing
  const lastAppliedVoiceTextRef = useRef<string>(''); // Track what voice text was last applied to avoid double insertion
  const baseTextRef = useRef<string>(''); // Track the base text (before any voice input) to prevent double insertion
  const lastVoiceUpdateRef = useRef<string>(''); // Track last voice update to detect manual edits
  const currentLanguage = useSelector((state: DefaultRootState) => state.app.language?.value || 'en');

  const stopVoiceTyping = useCallback(async () => {
    try {
      // Stop web speech recognition
      if (Platform.OS === 'web' && recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }

      // Stop native voice recognition
      if (Platform.OS !== 'web' && Voice) {
        try {
          await Voice.stop();
        } catch (stopError) {
          // Ignore errors when stopping (might already be stopped)
          console.log('Voice already stopped or error stopping:', stopError);
        }
        Voice.removeAllListeners();
        recognitionRef.current = null;
      }

      // Reset last processed text when stopping
      lastProcessedTextRef.current = '';
      setIsRecording(false);
    } catch (error) {
      console.error('Error stopping voice typing:', error);
      setIsRecording(false);
    }
  }, []);

  const startVoiceTyping = useCallback(async () => {
    try {
      // For web, use Web Speech API
      if (Platform.OS === 'web' && 'webkitSpeechRecognition' in (window as any)) {
        const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
        const recognition = new SpeechRecognition();

        recognition.continuous = true;
        recognition.interimResults = true;

        // Map i18n language codes to speech recognition language codes
        const languageMap: Record<string, string> = {
          'en': 'en-US',
          'es': 'es-ES',
          'de': 'de-DE',
          'fr': 'fr-FR',
          'it': 'it-IT',
          'ja': 'ja-JP',
          'ko': 'ko-KR',
          'zh': 'zh-CN',
          'ar': 'ar-SA',
          'el': 'el-GR',
        };

        recognition.lang = languageMap[currentLanguage] || 'en-US';

        recognition.onstart = () => {
          setIsRecording(true);
          // Clear ignore flag when starting new recording
          ignoreVoiceResultsRef.current = false;
          // Reset applied text tracking when starting new session
          lastAppliedVoiceTextRef.current = '';
          // Capture base text when starting new recording session
          const currentTextFromInput = getCurrentText ? getCurrentText() : (inputToolbarTextRef.current || '');
          baseTextRef.current = currentTextFromInput;
        };

        recognition.onresult = (event: any) => {
          // Ignore results if we've been cleared (e.g., after sending a message)
          if (ignoreVoiceResultsRef.current) {
            return;
          }
          
          let finalTranscript = '';
          let interimTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }

          if (finalTranscript || interimTranscript) {
            // If this is the first voice input in this session, capture the base text
            if (!lastAppliedVoiceTextRef.current) {
              const currentTextFromInput = getCurrentText ? getCurrentText() : (inputToolbarTextRef.current || '');
              baseTextRef.current = currentTextFromInput;
            }
            
            // Always append to the base text (not to text that might include previous voice input)
            const newText = baseTextRef.current ? `${baseTextRef.current} ${finalTranscript}` : finalTranscript;
            inputToolbarTextRef.current = newText;
            lastAppliedVoiceTextRef.current = newText; // Track what we're applying

            // For web, update the input field directly
            if (Platform.OS === 'web') {
              const fullText = newText + (interimTranscript || '');
              if (setTextInInput) {
                setTextInInput(fullText);
              } else {
                // Fallback: try to find input element
                const inputElement = document.querySelector('textarea, input[type="text"]') as HTMLInputElement | HTMLTextAreaElement;
                if (inputElement) {
                  inputElement.value = fullText;
                  inputElement.dispatchEvent(new Event('input', { bubbles: true }));
                }
              }
            } else {
              // For native, update via voiceInputText state which will be synced
              const fullText = newText + (interimTranscript || '');
              setVoiceInputText(fullText);
              lastVoiceUpdateRef.current = fullText; // Track this as a voice update
              if (onTextUpdate) {
                onTextUpdate(fullText);
              }
            }
          }
        };

        recognition.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          stopVoiceTyping();
          if (event.error !== 'no-speech') {
            Alert.alert('Voice Typing Error', `Could not process voice: ${event.error}`);
          }
        };

        recognition.onend = () => {
          if (isRecording) {
            // Restart if still recording (continuous mode)
            try {
              recognition.start();
            } catch (e) {
              stopVoiceTyping();
            }
          }
        };

        recognition.start();
        recognitionRef.current = recognition;
      } else {
        // For native platforms (iOS & Android) - use @react-native-voice/voice
        if (!Voice) {
          Alert.alert('Error', 'Voice recognition is not available on this platform.');
          return;
        }
        
        try {
          // Remove any existing listeners first to prevent duplicates
          Voice.removeAllListeners();

          // Map i18n language codes to Voice library language codes
          const languageMap: Record<string, string> = {
            'en': 'en-US',
            'es': 'es-ES',
            'de': 'de-DE',
            'fr': 'fr-FR',
            'it': 'it-IT',
            'ja': 'ja-JP',
            'ko': 'ko-KR',
            'zh': 'zh-CN',
            'ar': 'ar-SA',
            'el': 'el-GR',
          };

          const speechLanguage = languageMap[currentLanguage] || 'en-US';

          // Set up Voice event handlers
          Voice.onSpeechStart = () => {
            setIsRecording(true);
            // Reset last processed text when starting a new session
            lastProcessedTextRef.current = '';
            // Clear ignore flag when starting new recording
            ignoreVoiceResultsRef.current = false;
            // Reset applied text tracking when starting new session
            lastAppliedVoiceTextRef.current = '';
            // Capture base text when starting new recording session
            const currentTextFromInput = getCurrentText ? getCurrentText() : (inputToolbarTextRef.current || '');
            baseTextRef.current = currentTextFromInput;
          };

          Voice.onSpeechEnd = () => {
            // Will be handled by onSpeechResults
          };

          Voice.onSpeechError = (e: any) => {
            console.error('Voice recognition error:', e);
            // Ignore "No match" error (code 7) and "no-speech" errors (code 6) - these are normal
            if (e.error?.code === '7' || e.error?.code === '6') {
              // Just stop silently for these common errors
              setIsRecording(false);
              stopVoiceTyping();
              return;
            }
            setIsRecording(false);
            Alert.alert('Voice Input Error', e.error?.message || 'Could not process voice input.');
            stopVoiceTyping();
          };

          Voice.onSpeechResults = (e: any) => {
            // Ignore results if we've been cleared (e.g., after sending a message)
            if (ignoreVoiceResultsRef.current) {
              return;
            }
            
            if (e.value && e.value.length > 0) {
              const transcribedText = e.value[0]; // Use the first (most confident) result
              if (transcribedText && transcribedText.trim()) {
                const transcribedTextTrimmed = transcribedText.trim();

                // Check if this is new text or a duplicate of what we've already processed
                // If the transcribed text is the same as last processed, skip it
                if (transcribedTextTrimmed === lastProcessedTextRef.current) {
                  return;
                }

                // Extract only the new part by comparing with last processed text
                // Use word-boundary aware comparison to avoid cutting off parts of words
                let newPart = transcribedTextTrimmed;
                if (lastProcessedTextRef.current) {
                  const lastProcessed = lastProcessedTextRef.current.trim();
                  const newTranscription = transcribedTextTrimmed.trim();
                  
                  // Check if new transcription starts with last processed (exact match or word boundary)
                  if (newTranscription === lastProcessed) {
                    // Exact duplicate, skip
                    return;
                  } else if (newTranscription.startsWith(lastProcessed)) {
                    // New transcription extends the old one
                    // Check if the extension point is at a word boundary
                    const remaining = newTranscription.substring(lastProcessed.length);
                    
                    // If last processed ends with a space or is empty, or remaining starts with a space,
                    // it's safe to extract. Otherwise, we might be cutting a word.
                    const lastCharIsSpace = lastProcessed.length === 0 || lastProcessed[lastProcessed.length - 1] === ' ';
                    const remainingStartsWithSpace = remaining.length > 0 && remaining[0] === ' ';
                    
                    if (lastCharIsSpace || remainingStartsWithSpace) {
                      // Safe to extract - word boundary detected
                      newPart = remaining.trim();
                    } else {
                      // Not at word boundary - might be extending a word (e.g., "th" -> "this")
                      // Split into words to handle word-by-word comparison
                      const lastWords = lastProcessed.split(/\s+/).filter(w => w.length > 0);
                      const newWords = newTranscription.split(/\s+/).filter(w => w.length > 0);
                      
                      if (lastWords.length === 0) {
                        // No words in last processed, use full new transcription
                        newPart = newTranscription;
                      } else {
                        // Check if the last word of lastProcessed is being extended
                        const lastOldWord = lastWords[lastWords.length - 1];
                        const firstNewWord = newWords[0];
                        
                        // If the first new word starts with the last old word, it's being extended
                        if (firstNewWord && firstNewWord.startsWith(lastOldWord) && firstNewWord.length > lastOldWord.length) {
                          // Word is being extended - extract from the extended part of that word onwards
                          const extendedPart = firstNewWord.substring(lastOldWord.length);
                          const restOfWords = newWords.slice(1).join(' ');
                          newPart = extendedPart + (restOfWords ? ' ' + restOfWords : '');
                        } else if (newWords.length > lastWords.length) {
                          // New words added normally (word boundary was crossed)
                          newPart = newWords.slice(lastWords.length).join(' ');
                        } else if (newWords.length === lastWords.length) {
                          // Same number of words - check if last word is being extended
                          const lastWordOld = lastWords[lastWords.length - 1];
                          const lastWordNew = newWords[newWords.length - 1];
                          if (lastWordNew.startsWith(lastWordOld) && lastWordNew.length > lastWordOld.length) {
                            // Extract only the extended part
                            newPart = lastWordNew.substring(lastWordOld.length);
                          } else {
                            // Words changed, use full new transcription
                            newPart = newTranscription;
                          }
                        } else {
                          // Use full new transcription as fallback
                          newPart = newTranscription;
                        }
                      }
                    }
                  } else {
                    // New transcription doesn't start with old one - might be a correction
                    // Use the full new transcription
                    newPart = newTranscription;
                  }
                }

                // Only append if there's actually new content
                if (newPart) {
                  // If this is the first voice input in this session, capture the base text
                  if (!lastAppliedVoiceTextRef.current) {
                    const currentTextFromInput = getCurrentText ? getCurrentText() : (inputToolbarTextRef.current || '');
                    baseTextRef.current = currentTextFromInput;
                  }
                  
                  // Always append to the base text (not to text that might include previous voice input)
                  const newText = baseTextRef.current ? `${baseTextRef.current} ${newPart}` : newPart;
                  
                  inputToolbarTextRef.current = newText;
                  lastProcessedTextRef.current = transcribedTextTrimmed; // Update last processed
                  lastAppliedVoiceTextRef.current = newText; // Track what we're applying
                  
                  // Update voice input text which will trigger externalText update
                  setVoiceInputText(newText);
                  lastVoiceUpdateRef.current = newText; // Track this as a voice update
                  if (onTextUpdate) {
                    onTextUpdate(newText);
                  }
                }
              }
            }
          };

          Voice.onSpeechPartialResults = (e: any) => {
            // Ignore partial results if we've been cleared (e.g., after sending a message)
            if (ignoreVoiceResultsRef.current) {
              return;
            }
            
            // Show partial results in real-time (but don't overwrite if user is editing)
            if (e.value && e.value.length > 0) {
              const partialText = e.value[0];
              
              // If this is the first voice input in this session, capture the base text
              if (!lastAppliedVoiceTextRef.current) {
                const currentTextFromInput = getCurrentText ? getCurrentText() : (inputToolbarTextRef.current || '');
                baseTextRef.current = currentTextFromInput;
              }
              
              // Only update if we're still recording and haven't been manually edited
              // Show base text + new partial text for preview
              if (isRecording) {
                const previewText = baseTextRef.current ? `${baseTextRef.current} ${partialText}` : partialText;
                setVoiceInputText(previewText);
                lastVoiceUpdateRef.current = previewText; // Track this as a voice update
                if (onTextUpdate) {
                  onTextUpdate(previewText);
                }
              }
            }
          };

          // Start voice recognition
          await Voice.start(speechLanguage);
          recognitionRef.current = true;
          setIsRecording(true);
        } catch (error: any) {
          console.error('Error starting voice typing:', error);
          setIsRecording(false);
          if (error.code === 'E_PERMISSION_MISSING') {
            Alert.alert('Permission Required', 'Please grant microphone permission in your device settings.');
          } else {
            Alert.alert('Error', 'Failed to start voice typing. Please try again.');
          }
        }
      }
    } catch (error) {
      console.error('Error starting voice typing:', error);
      Alert.alert('Error', 'Failed to start voice typing. Please check microphone permissions.');
    }
  }, [currentLanguage, isRecording, stopVoiceTyping, getCurrentText, setTextInInput, onTextUpdate]);

  const handleVoiceButtonPress = useCallback(async () => {
    // Prevent starting voice input when input is disabled
    if (inputDisabled && !isRecording) {
      return;
    }
    
    if (isRecording) {
      await stopVoiceTyping();
    } else {
      await startVoiceTyping();
    }
  }, [isRecording, startVoiceTyping, stopVoiceTyping, inputDisabled]);

  // Stop voice recording when input becomes disabled
  useEffect(() => {
    if (inputDisabled && isRecording) {
      stopVoiceTyping();
    }
  }, [inputDisabled, isRecording, stopVoiceTyping]);

  const clearVoiceInput = useCallback(async () => {
    // Set flag to ignore any pending voice results
    ignoreVoiceResultsRef.current = true;
    
    // Stop recording if active
    if (isRecording) {
      await stopVoiceTyping();
    }
    
    // Clear all voice input state
    setVoiceInputText('');
    // Don't clear inputToolbarTextRef.current here - let it keep the current input value
    lastProcessedTextRef.current = '';
    lastAppliedVoiceTextRef.current = ''; // Reset applied text tracking
    baseTextRef.current = ''; // Reset base text tracking
    lastVoiceUpdateRef.current = ''; // Reset voice update tracking
  }, [isRecording, stopVoiceTyping]);

  // Function to handle manual text edits (should stop voice recording)
  const handleManualEdit = useCallback(async (text: string) => {
    // If we're recording, check if this is a manual edit (not from voice input)
    if (isRecording) {
      const isVoiceUpdate = text === lastVoiceUpdateRef.current || 
                           text === voiceInputText ||
                           (lastVoiceUpdateRef.current && text.startsWith(lastVoiceUpdateRef.current));
      
      // If this is NOT a voice update, it's a manual edit - stop recording
      if (!isVoiceUpdate) {
        // This is a manual edit - stop voice recording
        await stopVoiceTyping();
        // Clear voice input text since user is manually editing
        setVoiceInputText('');
        lastVoiceUpdateRef.current = '';
        baseTextRef.current = text; // Update base text to current manual input
        lastAppliedVoiceTextRef.current = ''; // Reset applied voice text tracking
      }
    }
    
    // Call the onManualEdit callback if provided
    if (onManualEdit) {
      onManualEdit(text);
    }
  }, [isRecording, stopVoiceTyping, voiceInputText, onManualEdit]);

  // Cleanup Voice listeners on unmount
  useEffect(() => {
    return () => {
      if (Platform.OS !== 'web' && Voice) {
        Voice.removeAllListeners();
        Voice.destroy().catch(() => { });
      }
    };
  }, []);

  return {
    isRecording,
    voiceInputText,
    startVoiceTyping,
    stopVoiceTyping,
    handleVoiceButtonPress,
    clearVoiceInput,
    handleManualEdit, // Expose manual edit handler
  };
};

